/**
 * RetirementPlannerV2 — readiness score, gap math, projection chart.
 * Uses recharts for the projection line.
 */
import { useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine } from "recharts";
import { NumberField, SelectField, ProgressBar, Money, StatCell } from "./primitives";
import { useFinancialPlan } from "@/context/FinancialPlanContext";
import { Button } from "@/components/ui/button";
import { useCurrency } from "@/context/CurrencyContext";

const LIFESTYLES = [["frugal", "Frugal"], ["comfortable", "Comfortable"], ["luxury", "Luxury"]];

export default function RetirementPlannerV2() {
  const { plan, summary, patch } = useFinancialPlan();
  const { currency: userCcy } = useCurrency();
  const currency = plan?.currency || userCcy;
  const r = plan?.retirement || {};
  const [form, setForm] = useState({
    desired_retirement_age: r.desired_retirement_age ?? 65,
    expected_annual_income_at_retirement: r.expected_annual_income_at_retirement ?? null,
    government_pension_monthly: r.government_pension_monthly ?? 0,
    employer_pension_monthly: r.employer_pension_monthly ?? 0,
    withdrawal_rate_pct: r.withdrawal_rate_pct ?? 4,
    desired_lifestyle: r.desired_lifestyle ?? "comfortable",
  });
  const [dirty, setDirty] = useState(false);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
  const save = async () => { await patch({ retirement: form }); setDirty(false); };

  const s = summary || {};
  const score = s.retirement_readiness_score ?? 0;
  const tone = score >= 90 ? "good" : score >= 60 ? "default" : "warn";
  const chartData = useMemo(() => buildProjection(plan, summary), [plan, summary]);

  return (
    <div className="space-y-4" data-testid="retirement-planner">
      {/* Big readiness score */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-baseline gap-3 mb-2">
          <div className={`text-5xl font-black tabular-nums ${score >= 90 ? "text-emerald-500" : score < 60 ? "text-amber-500" : ""}`} data-testid="retirement-score">
            {score}
          </div>
          <div className="text-lg text-muted-foreground">/ 100 readiness</div>
        </div>
        <ProgressBar value={score} tone={tone === "warn" ? "warn" : tone === "good" ? "good" : "primary"} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
          <StatCell label="Years remaining" value={s.years_until_retirement ?? "—"} testId="retire-years" />
          <StatCell label="Projected savings" value={<Money amount={s.projected_retirement_savings} currency={currency} />} testId="retire-projected" />
          <StatCell label="Nest egg needed" value={<Money amount={s.required_retirement_nest_egg} currency={currency} />} testId="retire-required" />
          <StatCell label="Gap" value={<Money amount={s.retirement_gap} currency={currency} />} sub={s.retirement_gap > 0 ? `save ${formatMoney(s.required_monthly_savings_for_target, currency)}/mo more` : "on track"} testId="retire-gap" tone={s.retirement_gap > 0 ? "warn" : "good"} />
        </div>
      </div>

      {/* Projection chart */}
      {chartData.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5" data-testid="retirement-chart">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Investment growth to retirement</div>
          <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <XAxis dataKey="age" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} formatter={(v) => formatMoney(v, currency)} />
                <ReferenceLine y={s.required_retirement_nest_egg} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" label={{ value: "Goal", position: "insideRight", fontSize: 10 }} />
                <Line type="monotone" dataKey="projected" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Editable form */}
      <div className="border border-border rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <NumberField label="Desired retirement age" value={form.desired_retirement_age} onChange={(v) => set("desired_retirement_age", v)} testId="retire-age-input" step="1" />
          <NumberField label="Expected annual income at retirement" value={form.expected_annual_income_at_retirement} onChange={(v) => set("expected_annual_income_at_retirement", v)} prefix={currency} testId="retire-income-input" />
          <NumberField label="Gov. pension (monthly)" value={form.government_pension_monthly} onChange={(v) => set("government_pension_monthly", v)} prefix={currency} testId="retire-gov-input" />
          <NumberField label="Employer pension (monthly)" value={form.employer_pension_monthly} onChange={(v) => set("employer_pension_monthly", v)} prefix={currency} testId="retire-emp-input" />
          <NumberField label="Withdrawal rate" value={form.withdrawal_rate_pct} onChange={(v) => set("withdrawal_rate_pct", v)} suffix="%" testId="retire-withdraw-input" />
          <SelectField label="Desired lifestyle" value={form.desired_lifestyle} onChange={(v) => set("desired_lifestyle", v)} options={LIFESTYLES} testId="retire-lifestyle-input" />
        </div>
        <div className="flex justify-end">
          <Button size="sm" onClick={save} disabled={!dirty} data-testid="retire-save-btn">Save</Button>
        </div>
      </div>
    </div>
  );
}

function buildProjection(plan, summary) {
  if (!plan || !summary?.years_until_retirement) return [];
  const years = summary.years_until_retirement;
  if (years <= 0) return [];
  const invested = summary.total_investments || 0;
  const monthly = summary.monthly_investing || 0;
  const rate = ((plan.assumptions?.default_return_pct ?? 7) - (plan.assumptions?.inflation_pct ?? 2)) / 100.0;
  const startAge = Math.max(20, Math.floor(((plan.retirement?.desired_retirement_age ?? 65) - years) || 30));
  const points = [];
  let balance = invested;
  for (let y = 0; y <= years; y++) {
    points.push({ age: startAge + y, projected: Math.round(balance) });
    balance = balance * (1 + rate) + monthly * 12;
  }
  return points;
}

function formatMoney(v, ccy) {
  if (v == null) return "—";
  const code = ccy || "USD";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: code, maximumFractionDigits: 0 }).format(v);
  } catch { return `${code} ${Math.round(v).toLocaleString()}`; }
}
